import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ChevronLeft,
  Copy,
  Eye,
  MessageCircle,
  RefreshCw,
  Search,
  Send,
  UserRound,
  X,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { GlobalContext } from "../../../Context/GlobalContext";
import useBhandarewala from "../../../services/Order/UseBhandarewala";
import useProfileDashboard from "../../../services/Settings/useProfileDashboard";
import useContacts from "../../../services/chat/useContacts";
import useChats from "../../../services/chat/useChats";
import ChatWindow from "../../whatsapp/Chats/ChatWindow";
import whatsappBg from "../../../images/chat_BG.png";

const CREATE_ORDER_USER_ID = "A1437";
const DEFAULT_RESTAURANT_NUMBER = "918587052590";
const BULK_ORDER_URL = "https://catalog.anlook.com/bulk-food/setup";
const CATALOG_ORIGIN = new URL(BULK_ORDER_URL).origin;

const getTenDigitMobile = (value) => {
  let digits = String(value ?? "").replace(/\D/g, "");

  if (digits.startsWith("0091") && digits.length >= 14) {
    digits = digits.slice(4);
  } else if (digits.startsWith("091") && digits.length >= 13) {
    digits = digits.slice(3);
  } else if (digits.startsWith("91") && digits.length >= 12) {
    digits = digits.slice(2);
  }

  if (digits.length === 11 && digits.startsWith("0")) {
    digits = digits.slice(1);
  }

  return digits.slice(0, 10);
};

const getUrlMobile = (value) => {
  const mobile = getTenDigitMobile(value);
  return /^\d{10}$/.test(mobile) ? `91${mobile}` : mobile;
};

const getRestaurantNumberFromReceipt = (receipt = {}) => {
  const rawNumber =
    receipt?.phone_no ||
    receipt?.phoneNo ||
    receipt?.phone ||
    receipt?.mobile ||
    receipt?.mobile_no ||
    receipt?.mobileNo ||
    receipt?.restaurant_mobile ||
    receipt?.organization_mobile ||
    receipt?.shop_mobile ||
    receipt?.contact_number ||
    receipt?.contactNo ||
    receipt?.contact_no ||
    receipt?.business_mobile ||
    "";

  const normalized = getUrlMobile(rawNumber);

  return /^91\d{10}$/.test(normalized) ? normalized : DEFAULT_RESTAURANT_NUMBER;
};

const safeParseJson = (value, fallback) => {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const normalizeContactNumber = (value) =>
  String(value || "").replace(/\D/g, "");

const getContactName = (contact) =>
  contact?.contact_name || contact?.name || contact?.customer_name || "Unknown";

const getContactNumber = (contact) =>
  contact?.contact_number ||
  contact?.mobile ||
  contact?.phone ||
  contact?.customer_mobile ||
  "";

const getUnreadCount = (contact) =>
  Number(
    contact?.unread_count ||
      contact?.unreadCount ||
      contact?.unread ||
      contact?.message_count ||
      0,
  ) || 0;

const getLastMessage = (contact) =>
  contact?.last_message ||
  contact?.lastMessage ||
  contact?.message ||
  contact?.last_msg ||
  "Hi";

const getContactTime = (contact) => {
  const raw =
    contact?.updated_at ||
    contact?.updatedAt ||
    contact?.last_message_time ||
    contact?.lastMessageTime ||
    contact?.created_at;

  if (!raw) return "";

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const copyToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
};

const CreateOrderModal = ({ onClose, onStartOrder, loading }) => {
  const [form, setForm] = useState({ name: "", mobile: "" });

  const updateField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: field === "mobile" ? getTenDigitMobile(value) : value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const name = form.name.trim();
    const mobile = getTenDigitMobile(form.mobile);

    if (!name) {
      toast.error("Please enter customer name");
      return;
    }

    if (!/^\d{10}$/.test(mobile)) {
      toast.error("Enter valid 10 digit mobile number");
      return;
    }

    onStartOrder({ name, mobile });
  };

  return (
    <div
      className="fixed inset-0 z-[150] flex items-center justify-center bg-black/40 p-4 montserrat-font-1"
      onClick={() => {
        if (!loading) onClose();
      }}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-gray-800">
              Create Order
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              Enter customer details to start bulk order
            </p>
          </div>

          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-gray-700">
              Name
            </span>
            <input
              type="text"
              value={form.name}
              onChange={(event) => updateField("name", event.target.value)}
              placeholder="Enter customer name"
              disabled={loading}
              autoComplete="name"
              className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none transition focus:border-green-500 focus:ring-2 focus:ring-green-100 disabled:bg-gray-100"
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-gray-700">
              Mobile
            </span>
            <input
              type="tel"
              inputMode="numeric"
              value={form.mobile}
              onChange={(event) => updateField("mobile", event.target.value)}
              placeholder="9810964409"
              disabled={loading}
              autoComplete="tel"
              className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none transition focus:border-green-500 focus:ring-2 focus:ring-green-100 disabled:bg-gray-100"
            />
            <p className="mt-1 text-[11px] text-gray-500">
              Enter only 10 digit mobile number.
            </p>
          </label>
        </div>

        <div className="flex justify-end gap-3 border-t px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="h-10 rounded-lg border border-gray-300 px-4 text-sm font-medium text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={loading}
            className="inline-flex h-10 min-w-[120px] items-center justify-center rounded-lg bg-green-500 px-4 text-sm font-semibold text-white transition hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? "Starting..." : "Start Order"}
          </button>
        </div>
      </form>
    </div>
  );
};

const ContactAvatar = ({ contact, size = "h-10 w-10" }) => {
  const name = getContactName(contact);
  const firstLetter =
    name && name !== "Unknown" ? name.charAt(0).toUpperCase() : "";

  return (
    <div
      className={`${size} shrink-0 rounded-full bg-slate-400 text-white flex items-center justify-center font-semibold`}
    >
      {firstLetter || <UserRound size={18} />}
    </div>
  );
};

const BulkOrderWorkspace = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userID } = useContext(GlobalContext);

  const { generateBulkOrderNumber } = useBhandarewala();
  const { fetchReceiptDetails } = useProfileDashboard();

  const {
    chatContacts,
    setChatContacts,
    fetchChatContacts,
    loading: contactsLoading,
  } = useContacts();

  const { chats, loadingChats, fetchChats, sendMessage } = useChats();

  const [showCreateModal, setShowCreateModal] = useState(true);
  const [createOrderLoading, setCreateOrderLoading] = useState(false);
  const [receiptDetails, setReceiptDetails] = useState(
    location.state?.receiptDetails || {},
  );

  const [catalogUrl, setCatalogUrl] = useState("");
  const [orderInfo, setOrderInfo] = useState(null);
  const [orderCompleted, setOrderCompleted] = useState(false);
  const [shareText, setShareText] = useState("");

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarMode, setSidebarMode] = useState("contacts");
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactSearch, setContactSearch] = useState("");
  const [draftMessage, setDraftMessage] = useState("");
  const [pendingPasteText, setPendingPasteText] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [chatPage, setChatPage] = useState(1);
  const [chatHasMore, setChatHasMore] = useState(true);

  const lastWsMessageRef = useRef("");

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, []);

  useEffect(() => {
    if (String(userID || "") && String(userID || "") !== CREATE_ORDER_USER_ID) {
      toast.error("Create Order is not available for this user");
      navigate("/orders", { replace: true });
    }
  }, [userID, navigate]);

  useEffect(() => {
    if (!userID) return;

    const cacheKey = `chat_contacts_${userID}`;
    const cached = sessionStorage.getItem(cacheKey);

    if (cached && (!chatContacts || chatContacts.length === 0)) {
      const parsed = safeParseJson(cached, []);
      if (Array.isArray(parsed)) {
        setChatContacts(parsed);
        return;
      }
    }

    if (!cached && (!chatContacts || chatContacts.length === 0)) {
      fetchChatContacts(1, 50);
    }
  }, [userID]);

  useEffect(() => {
    const hasReceiptValue = Object.keys(receiptDetails || {}).length > 0;
    if (!userID || hasReceiptValue) return;

    const loadReceipt = async () => {
      try {
        const res = await fetchReceiptDetails(userID);
        const receipt =
          res?.response?.[0] ||
          res?.data?.[0] ||
          res?.response ||
          res?.data ||
          res ||
          {};

        if (receipt && typeof receipt === "object") {
          setReceiptDetails(receipt);
        }
      } catch (error) {
        console.error("Receipt API error:", error);
      }
    };

    loadReceipt();
  }, [userID, receiptDetails]);

  useEffect(() => {
    const handleCatalogMessage = (event) => {
      if (event.origin !== CATALOG_ORIGIN) return;

      const payload = event?.data;
      if (!payload || typeof payload !== "object") return;

      if (payload.type === "BULK_ORDER_COMPLETED") {
        const finalText =
          String(payload.copyText || "").trim() ||
          shareText ||
          `Bulk order #${orderInfo?.orderId || ""}`.trim();

        setOrderCompleted(true);
        setShareText(finalText);

        if (payload.orderId) {
          setOrderInfo((prev) => ({
            ...(prev || {}),
            orderId: String(payload.orderId),
          }));
        }
      }

      if (payload.type === "BULK_ORDER_SHARE_TEXT") {
        const finalText = String(payload.copyText || "").trim();
        if (finalText) setShareText(finalText);
      }
    };

    window.addEventListener("message", handleCatalogMessage);
    return () => window.removeEventListener("message", handleCatalogMessage);
  }, [orderInfo?.orderId, shareText]);

  useEffect(() => {
    if (!userID || !selectedContact) return;

    const contactNumber = getContactNumber(selectedContact);
    if (!contactNumber) return;

    const socket = new WebSocket(
      `wss://payinnew.remittpe.com/ws?userId=${userID}&roomId=${contactNumber}`,
    );

    socket.onmessage = (event) => {
      if (lastWsMessageRef.current === event.data) return;
      lastWsMessageRef.current = event.data;
      fetchChats(contactNumber, 1);
    };

    return () => socket.close();
  }, [userID, selectedContact]);

  const filteredContacts = useMemo(() => {
    const query = contactSearch.trim().toLowerCase();
    const list = Array.isArray(chatContacts) ? chatContacts : [];

    if (!query) return list;

    return list.filter((contact) => {
      const name = getContactName(contact).toLowerCase();
      const number = getContactNumber(contact).toLowerCase();
      return name.includes(query) || number.includes(query);
    });
  }, [chatContacts, contactSearch]);

  const handleStartOrder = async ({ name, mobile }) => {
    if (String(userID || "") !== CREATE_ORDER_USER_ID) {
      toast.error("Create Order is not available for this user");
      return;
    }

    if (createOrderLoading) return;

    const customerMobile = getTenDigitMobile(mobile);

    if (!/^\d{10}$/.test(customerMobile)) {
      toast.error("Enter valid 10 digit mobile number");
      return;
    }

    try {
      setCreateOrderLoading(true);

      const result = await generateBulkOrderNumber({
        userID,
        mobile: customerMobile,
      });

      if (!result?.success || !result?.orderId) {
        return;
      }

      const restaurantNumber = getRestaurantNumberFromReceipt(receiptDetails);
      const url = new URL(BULK_ORDER_URL);

      url.searchParams.set("mobile", `91${customerMobile}`);
      url.searchParams.set("userid", userID);
      url.searchParams.set("orderid", result.orderId);
      url.searchParams.set("restaurantNumber", restaurantNumber);
      url.searchParams.set("orderType", "home_delivery");
      url.searchParams.set("orderFrom", "chaafo");
      url.searchParams.set("name", name.trim());
      url.searchParams.set("isReferred", "true");

      const defaultShareText = `Order #${result.orderId} - ${name.trim()} (${customerMobile})`;

      setOrderInfo({
        orderId: String(result.orderId),
        name: name.trim(),
        mobile: customerMobile,
      });
      setShareText(defaultShareText);
      setCatalogUrl(url.toString());
      setShowCreateModal(false);
      setSidebarOpen(true);
      setSidebarMode("contacts");
    } catch (error) {
      console.error("Create order error:", error);
      toast.error(error?.message || "Unable to start order");
    } finally {
      setCreateOrderLoading(false);
    }
  };

  const handleSelectContact = async (contact) => {
    const contactNumber = getContactNumber(contact);
    if (!contactNumber) {
      toast.error("Contact number is missing");
      return;
    }

    setSelectedContact(contact);
    setSidebarMode("chat");
    setSidebarOpen(true);
    setChatPage(1);
    setChatHasMore(true);

    if (pendingPasteText) {
      setDraftMessage(pendingPasteText);
      setPendingPasteText("");
    }

    try {
      await fetchChats(contactNumber, 1);
    } catch (error) {
      console.error("Chat load error:", error);
      toast.error("Unable to load chat");
    }
  };

  const handleFetchMoreChats = async () => {
    if (!selectedContact || loadingChats || !chatHasMore) return;

    const contactNumber = getContactNumber(selectedContact);
    const nextPage = chatPage + 1;
    const res = await fetchChats(contactNumber, nextPage);

    if (!res?.data || res.data.length === 0) {
      setChatHasMore(false);
      return;
    }

    setChatPage(nextPage);
  };

  const handleSendMessage = async (event) => {
    event?.preventDefault?.();

    if (!selectedContact || !draftMessage.trim() || sendingMessage) return;

    const contactNumber = getContactNumber(selectedContact);
    const message = draftMessage.trim();

    try {
      setSendingMessage(true);
      await sendMessage(contactNumber, message);
      setDraftMessage("");
      await fetchChats(contactNumber, 1);
    } catch (error) {
      console.error("Send message error:", error);
      toast.error("Unable to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleCopyAndOpenChat = async () => {
    const text = String(shareText || "").trim();

    if (!text) {
      toast.error("Nothing to copy yet");
      return;
    }

    try {
      await copyToClipboard(text);
    } catch (error) {
      console.error("Clipboard error:", error);
    }

    setSidebarOpen(true);

    if (selectedContact) {
      setSidebarMode("chat");
      setDraftMessage(text);
      toast.success("Copied and added to chat box");
      return;
    }

    setSidebarMode("contacts");
    setPendingPasteText(text);
    toast.success("Copied. Select a contact to paste it into chat");
  };

  const handleOpenChat = () => {
    setSidebarOpen(true);
    setSidebarMode(selectedContact ? "chat" : "contacts");
  };

  const handleViewOnly = () => {
    setSidebarOpen(false);
  };

  const handleRefreshContacts = async () => {
    try {
      await fetchChatContacts(1, 50, true);
    } catch (error) {
      console.error("Contacts refresh error:", error);
    }
  };

  const handleCloseRoute = () => {
    navigate("/orders");
  };

  return (
    <div className="fixed inset-0 z-[120] flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden bg-[#f3f4f6] montserrat-font-1">
      {showCreateModal && (
        <CreateOrderModal
          onClose={handleCloseRoute}
          onStartOrder={handleStartOrder}
          loading={createOrderLoading}
        />
      )}

      <div className="flex h-14 shrink-0 items-center justify-between border-b bg-white px-4 shadow-sm">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={handleCloseRoute}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
            title="Back to orders"
          >
            <ArrowLeft size={18} />
          </button>

          <div className="min-w-0">
            <h1 className="truncate text-sm font-semibold text-gray-800">
              Bulk Order Workspace
            </h1>
            <p className="truncate text-xs text-gray-500">
              {orderInfo?.orderId
                ? `Order #${orderInfo.orderId} • ${orderInfo.name} • ${orderInfo.mobile}`
                : "Create an order to open catalogue and chat"}
            </p>
          </div>
        </div>

        {orderCompleted && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleOpenChat}
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
            >
              <MessageCircle size={15} />
              Chat
            </button>
            <button
              type="button"
              onClick={handleViewOnly}
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              <Eye size={15} />
              View
            </button>
          </div>
        )}
      </div>

      <div
        className="grid min-h-0 flex-1 overflow-hidden transition-[grid-template-columns] duration-200"
        style={{
          gridTemplateColumns: sidebarOpen
            ? "minmax(0, 7fr) minmax(340px, 3fr)"
            : "minmax(0, 1fr)",
        }}
      >
        <section className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden bg-white">
          <div className="min-h-0 flex-1 overflow-hidden">
            {catalogUrl ? (
              <iframe
                src={catalogUrl}
                title="Bulk food catalogue"
                className="h-full w-full border-0"
                allow="clipboard-read; clipboard-write"
                referrerPolicy="strict-origin-when-cross-origin"
              />
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-gray-500">
                Create order to open catalogue.
              </div>
            )}
          </div>

          {catalogUrl && (
            <div className="flex h-14 shrink-0 items-center justify-end gap-2 border-t border-gray-200 bg-white px-4 shadow-[0_-4px_14px_rgba(15,23,42,0.06)]">
              <button
                type="button"
                onClick={handleCopyAndOpenChat}
                className="inline-flex h-10 items-center gap-2 rounded-lg bg-emerald-600 px-4 text-sm font-semibold text-white transition hover:bg-emerald-700"
                title="Copy order details and open chat"
              >
                <Copy size={16} />
                Copy & Chat
              </button>

              {orderCompleted && (
                <>
                  <button
                    type="button"
                    onClick={handleOpenChat}
                    className="inline-flex h-10 items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 text-sm font-semibold text-emerald-700 hover:bg-emerald-100"
                  >
                    <MessageCircle size={16} />
                    Chat
                  </button>

                  <button
                    type="button"
                    onClick={handleViewOnly}
                    className="inline-flex h-10 items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    <Eye size={16} />
                    View
                  </button>
                </>
              )}
            </div>
          )}
        </section>

        {sidebarOpen && (
          <aside className="h-full min-h-0 min-w-0 overflow-hidden border-l border-gray-200 bg-white shadow-xl">
            {sidebarMode === "contacts" && (
              <div className="flex h-full min-h-0 flex-col overflow-hidden">
                <div className="flex h-14 shrink-0 items-center justify-between border-b px-4">
                  <div>
                    <p className="text-xs font-bold tracking-wide text-slate-500">
                      RECENT CHATS
                    </p>
                    <p className="text-[11px] text-gray-400">
                      Select a contact to open chat
                    </p>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={handleRefreshContacts}
                      disabled={contactsLoading}
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-emerald-600 hover:bg-gray-50 disabled:opacity-50"
                      title="Refresh contacts"
                    >
                      <RefreshCw
                        size={15}
                        className={contactsLoading ? "animate-spin" : ""}
                      />
                    </button>

                    <button
                      type="button"
                      onClick={() => setSidebarOpen(false)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                      title="Close chat panel"
                    >
                      <X size={17} />
                    </button>
                  </div>
                </div>

                <div className="shrink-0 border-b p-2.5">
                  <div className="flex h-9 items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 focus-within:border-emerald-400 focus-within:bg-white">
                    <Search size={15} className="text-gray-400" />
                    <input
                      type="text"
                      value={contactSearch}
                      onChange={(event) => setContactSearch(event.target.value)}
                      placeholder="Search contact..."
                      className="min-w-0 flex-1 bg-transparent text-sm outline-none"
                    />
                  </div>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                  {contactsLoading && filteredContacts.length === 0 ? (
                    <div className="flex h-32 items-center justify-center text-xs text-gray-400">
                      Loading contacts...
                    </div>
                  ) : filteredContacts.length === 0 ? (
                    <div className="flex h-32 items-center justify-center px-4 text-center text-xs text-gray-400">
                      No chat contacts found.
                    </div>
                  ) : (
                    filteredContacts.map((contact, index) => {
                      const number = getContactNumber(contact);
                      const name = getContactName(contact);
                      const unread = getUnreadCount(contact);
                      const isSelected =
                        normalizeContactNumber(number) ===
                        normalizeContactNumber(
                          getContactNumber(selectedContact),
                        );

                      return (
                        <button
                          key={contact?.id || number || index}
                          type="button"
                          onClick={() => handleSelectContact(contact)}
                          className={`flex w-full items-center gap-3 border-b px-3 py-2.5 text-left transition hover:bg-gray-50 ${
                            isSelected ? "bg-emerald-50" : "bg-white"
                          }`}
                        >
                          <ContactAvatar contact={contact} />

                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <p className="truncate text-[13px] font-medium text-gray-800">
                                {name} {number ? `| ${number}` : ""}
                              </p>
                              {unread > 0 && (
                                <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-emerald-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                                  {unread > 99 ? "99+" : unread}
                                </span>
                              )}
                            </div>

                            <div className="mt-0.5 flex items-center justify-between gap-2">
                              <p className="truncate text-[11px] text-gray-500">
                                {getLastMessage(contact)}
                              </p>
                              <span className="shrink-0 text-[10px] text-gray-400">
                                {getContactTime(contact)}
                              </span>
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {sidebarMode === "chat" && selectedContact && (
              <div className="flex h-full min-h-0 flex-col overflow-hidden bg-white">
                <div className="flex h-14 shrink-0 items-center gap-2 border-b bg-emerald-600 px-2.5 text-white">
                  <button
                    type="button"
                    onClick={() => setSidebarMode("contacts")}
                    className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-white/15"
                    title="Back to contacts"
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <ContactAvatar contact={selectedContact} size="h-9 w-9" />

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">
                      {getContactName(selectedContact)}
                    </p>
                    <p className="truncate text-[11px] text-emerald-50">
                      {getContactNumber(selectedContact)}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setSidebarOpen(false)}
                    className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-white/15"
                    title="Close chat"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="relative min-h-0 flex-1 overflow-hidden bg-[#fff8e8]">
                  <div
                    className="absolute inset-0 bg-repeat bg-center opacity-60"
                    style={{ backgroundImage: `url(${whatsappBg})` }}
                  />
                  <div className="absolute inset-0 overflow-y-auto">
                    <ChatWindow
                      chats={chats}
                      isLoadingChats={loadingChats}
                      chatHasMore={chatHasMore}
                      fetchMoreChats={handleFetchMoreChats}
                    />
                  </div>
                </div>

                <form
                  onSubmit={handleSendMessage}
                  className="flex shrink-0 items-center gap-2 border-t bg-white p-2.5"
                >
                  <input
                    type="text"
                    value={draftMessage}
                    onChange={(event) => setDraftMessage(event.target.value)}
                    placeholder="Type a message"
                    className="h-10 min-w-0 flex-1 rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                  />

                  <button
                    type="submit"
                    disabled={!draftMessage.trim() || sendingMessage}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
                    title="Send message"
                  >
                    <Send size={18} />
                  </button>
                </form>
              </div>
            )}
          </aside>
        )}
      </div>
    </div>
  );
};

export default BulkOrderWorkspace;
